/**
 * math-exam-gen — 數學「出考卷產生器」(核心交付,boss「正式段考卷替代」:參考真卷
 * 題型 + 課本 → 自己產生整張數學段考卷,可重抽,總分 100)。
 *
 * 真卷題型參考:public/data/math-exam-bank-v1.json(三下「乘法與除法」段考真卷)。
 * 逆向出七大題(段考卷規模,Σ 配分 = 100):
 *   ① 直式除法 + 驗算(compute)     —— 802 ÷ 7 = ?(整除/有餘混合,有餘可驗算)。
 *   ② 乘法直式(mulvert)            —— 三位數 × 一位數直式,答積(真卷「乘法與除法」必有)。
 *   ③ 算式填空(arrow chain)        —— 210 →(÷5)→ ? →(×5)→ ? round-trip。
 *   ④ 倍數互換 / 乘除互逆(inverse) —— 28÷7=4,則 28÷4=?、7×4=?(含大數梯度)。
 *   ⑤ 有餘數應用題(remword)        —— 進位(幾台車)/ 捨去剩餘(可分幾人、剩幾顆)。
 *   ⑥ 兩步驟文字題(twostep)        —— 先乘再除 / 先加再分,真的兩步驟。
 *   ⑦ 平分・倍數應用題(word)       —— 單步除法 / 乘法應用題。
 *
 * 架構 = 聽力卷產生器(listening-exam-gen.ts)同一套:**池 + 範本 + 硬規則 lockdown
 * + validator + 200-seed regression**。
 *
 * 鐵則 / 設計:
 *   - **seeded RNG**(別用 Math.random):同 seed → 同卷,可重現;UI「重抽」= 換 seed。
 *   - **答案程式可驗證**:每題的 answer 都能由產生器反算(a=b×q+r、round-trip 回原數、
 *     乘除互逆、有餘情境的進位/捨去、兩步驟逐步重算),validateMathExam 重算後比對。
 *   - **數值鎖在三下(G3)範圍**:被除數 ≤ 999、除數 2–9、乘積 ≤ 9999、無除以 0。
 *     compute 約 40% 整除 / 60% 有餘;互逆題用整除(③④)。
 *   - **整卷 Σ(pointsEach×count)= 100**(正式段考卷)。
 *   - **lockdown 防壞題**(memory feedback_generator_source_lockdown,不打地鼠):
 *       1. 情境池 / 範本是策展閉集(WORD_SCENARIOS / REMWORD_SCENARIOS / TWOSTEP_SCENARIOS / ARROW_OPS)。
 *       2. 硬規則寫進產生器(G3 範圍、非 0 除數、round-trip 一致、餘數情境正確)。
 *       3. `validateMathExam` 驗證器(回錯誤清單,空 = 合法)。
 *       4. 200-seed regression test 全過(tests/learn/math-exam-gen.test.ts)。
 *
 * 學習/遊戲分離:純學習側,**絕不**碰 commanderXp / 戰鬥數值。
 */

// ───────────────────────────── 型別 ─────────────────────────────

/** 大題種類(七大題)。 */
export type MathSectionKind =
  | 'compute'
  | 'mulvert'
  | 'arrow'
  | 'inverse'
  | 'remword'
  | 'twostep'
  | 'word';

/**
 * ① 直式除法 + 驗算:`a ÷ b`,答商 + 餘。
 * 整除題 remainder=0(prompt「= ?」、answer=純商、check=商×除數=被除數);
 * 有餘題 remainder ≥ 1(prompt「= ? 餘 ?」、可練驗算)。
 */
export interface MathComputeItem {
  id: string;
  kind: 'compute';
  /** 被除數。 */
  dividend: number;
  /** 除數(2–9)。 */
  divisor: number;
  /** 商。 */
  quotient: number;
  /** 餘數(整除題 = 0;有餘題 1..除數-1)。 */
  remainder: number;
  /** 題幹(印 / 顯示用)。 */
  prompt: string;
  /** 正解人類可讀字串(顯示 / 批改用)。 */
  answer: string;
  /** 驗算式(有餘:商×除數+餘=被除數;整除:商×除數=被除數)。 */
  check: string;
}

/** ② 乘法直式:`a × b`(三位數 × 一位數),答積。 */
export interface MathMulVertItem {
  id: string;
  kind: 'mulvert';
  /** 被乘數(三位數,100–999)。 */
  factorA: number;
  /** 乘數(2–9,一位數)。 */
  factorB: number;
  /** 積(= factorA × factorB,≤ 9999)。 */
  product: number;
  prompt: string;
  /** 正解 `積`(顯示用)。 */
  answer: string;
  /** 驗算式(被乘數×乘數=積)。 */
  check: string;
}

/** ③ 算式填空(arrow chain):`N →(op1)→ ? →(op2)→ ?`,op2 = op1 的逆 → 第二格回原數。 */
export interface MathArrowItem {
  id: string;
  kind: 'arrow';
  /** 起始數。 */
  start: number;
  /** 第一步運算符號(× / ÷)。 */
  op: '×' | '÷';
  /** 倍數 / 除數。 */
  factor: number;
  /** 第一格答案(start op factor)。 */
  mid: number;
  /** 第二格答案(逆運算後 = start)。 */
  end: number;
  prompt: string;
  /** 正解 `mid;end`(顯示用)。 */
  answer: string;
}

/** ④ 倍數互換 / 乘除互逆:給 `a÷b=c`,問 `a÷c=?` 與 `b×c=?`。含大數梯度題。 */
export interface MathInverseItem {
  id: string;
  kind: 'inverse';
  /** 被除數 = b×c。 */
  product: number;
  /** 除數一。 */
  divisorB: number;
  /** 商一(= 除數二)。 */
  quotientC: number;
  prompt: string;
  /** 正解 `a÷c=b;b×c=a`(顯示用,兩格)。 */
  answer: string;
}

/**
 * ⑤ 有餘數應用題:情境決定餘數怎麼處理(三下除法核心鑑別)。
 *   - 'ceil'(進位):要全部裝完 → 商有餘要多一份(車/船/箱)→ 答 = 商 + 1。
 *   - 'floor'(捨去 + 剩餘):平分到不能再分 → 答 = 商(可分幾人/幾組)+ 剩 remainder。
 */
export interface MathRemWordItem {
  id: string;
  kind: 'remword';
  /** 被除數(總量)。 */
  dividend: number;
  /** 除數(每份量,≥ 5)。 */
  divisor: number;
  /** 商(整數)。 */
  quotient: number;
  /** 餘數(保證 ≥ 1,情境才成立)。 */
  remainder: number;
  /** 餘數處理:進位 / 捨去。 */
  mode: 'ceil' | 'floor';
  /** 數值答案(ceil = 商+1;floor = 商)。 */
  value: number;
  /** 答案單位。 */
  unitName: string;
  prompt: string;
  /** 正解字串(ceil:`N 單位`;floor:`N 單位,剩 R 餘位`)。 */
  answer: string;
}

/**
 * ⑥ 兩步驟文字題:真的兩步驟,答案逐步可驗證。
 *   - 'mul-div'(先乘再除):total = a×b,再 ÷ c,答 total/c(整除)。
 *   - 'add-div'(先加再分):total = a+b,再 ÷ c,答 total/c(整除)。
 */
export interface MathTwoStepItem {
  id: string;
  kind: 'twostep';
  /** 兩步驟形態。 */
  shape: 'mul-div' | 'add-div';
  /** 第一運算元。 */
  a: number;
  /** 第二運算元。 */
  b: number;
  /** 第二步除數。 */
  c: number;
  /** 第一步中間結果(a×b 或 a+b)。 */
  step1: number;
  /** 最終答案(step1 ÷ c,整除)。 */
  value: number;
  /** 單位。 */
  unitName: string;
  /** 完整算式(顯示 / 驗算用,如 `12×8÷6`、`(35+13)÷6`)。 */
  expr: string;
  prompt: string;
  /** 正解 `value 單位`(顯示用)。 */
  answer: string;
}

/** ⑦ 應用題:平分 / 倍數情境,單步除法或乘法。 */
export interface MathWordItem {
  id: string;
  kind: 'word';
  prompt: string;
  /** 算式(如 `545÷5`)。 */
  expr: string;
  /** 數值答案。 */
  value: number;
  /** 單位(元 / 個 / 顆…)。 */
  unitName: string;
  /** 正解 `value 單位`(顯示用)。 */
  answer: string;
}

export type MathExamItem =
  | MathComputeItem
  | MathMulVertItem
  | MathArrowItem
  | MathInverseItem
  | MathRemWordItem
  | MathTwoStepItem
  | MathWordItem;

/** 一大題 = 標籤 + 標題 + 指示語 + 每題配分 + 題目陣列。 */
export interface MathSection {
  kind: MathSectionKind;
  /** 大題標籤,例 '一'。 */
  label: string;
  /** 大題標題,例 '直式除法與驗算'。 */
  title: string;
  /** 作答指示語。 */
  instructions: string;
  /** 每題配分。 */
  pointsEach: number;
  items: MathExamItem[];
}

/** 一份數學考卷。 */
export interface MathExam {
  /** 卷 id,例 'math-12345'。 */
  id: string;
  /** 卷名,例 '數學考卷 · 第 12345 回'。 */
  title: string;
  /** 教科書版本(數學 = 康軒,見 project_textbook_versions)。 */
  textbook: '康軒';
  /** 範圍標註(顯示用)。 */
  scope: string;
  sections: MathSection[];
}

// ───────────────────────────── opts ─────────────────────────────

/** 各大題題數(預設見 DEFAULT_COUNTS,湊 100 分)。 */
export interface SectionCounts {
  compute?: number;
  mulvert?: number;
  arrow?: number;
  inverse?: number;
  remword?: number;
  twostep?: number;
  word?: number;
}

/** `generateMathExam` 選項。 */
export interface MathGenOpts {
  /** 可重現種子(同 seed 同卷);UI「重抽」= 換 seed。 */
  seed: number;
  /** 各大題題數,預設見 DEFAULT_COUNTS。 */
  counts?: SectionCounts;
  /**
   * 可選:限定要出的大題(kp 子集)。預設七大題全出。
   */
  kps?: MathSectionKind[];
  /** 卷 id(預設 `math-<seed>`)。 */
  id?: string;
  /** 卷名(預設 `數學考卷 · 第 <seed> 回`)。 */
  title?: string;
}

// ───────────────────────────── G3 範圍護欄(硬規則)─────────────────────────────

/** 三下數學數值上限(被除數 / 乘積)。 */
const MAX_DIVIDEND = 999;
const MAX_PRODUCT = 9999;
/** 除數 / 倍數合法範圍(一位非 1)。 */
const MIN_DIVISOR = 2;
const MAX_DIVISOR = 9;
/** 有餘數應用題的除數下限(別出「每箱裝 2 個」這種不真實情境)。 */
const REMWORD_MIN_DIVISOR = 5;

// ───────────────────────────── 情境池(策展閉集)─────────────────────────────

/**
 * ⑦ 應用題情境池 —— 人工策展閉集。每個情境是一個模板函式,給整除參數產生一句通順
 * 中文題 + 算式 + 單位。
 *   - op='div':除法(總量÷每份=份數 或 總量÷份數=每份)。`priced` 標記「單價型」
 *     (除數是單價,需用較高範圍,別「每包 2 元色紙」)。
 *   - op='mul':乘法(每份×份數=總量)。
 * 產生器只能從此池抽,杜絕硬規則外的隨機句。
 */
interface WordScenario {
  /** 'div' = 除法(總量已知);'mul' = 乘法(每份×份數)。 */
  op: 'div' | 'mul';
  /** 單位(答案單位)。 */
  unit: string;
  /** 'priced' = 除數為單價,須用較高範圍(5–20),避免「每包 2 元」。 */
  priced?: boolean;
  /**
   * 造句:除法 (total, part) → 答 total/part;乘法 (each, count) → 答 each*count。
   * 兩參數對除法 = (被除數, 除數);對乘法 = (每份, 份數)。
   */
  build: (a: number, b: number) => string;
}

const WORD_SCENARIOS: readonly WordScenario[] = [
  {
    op: 'div',
    unit: '元',
    build: (total, part) =>
      `媽媽買了 ${part} 份相同的點心,一共花了 ${total} 元,每份點心多少元?`,
  },
  {
    op: 'div',
    unit: '包',
    priced: true,
    build: (total, part) =>
      `文具店每包色紙賣 ${part} 元,小華付了 ${total} 元,可以買到幾包色紙?`,
  },
  {
    op: 'div',
    unit: '個',
    build: (total, part) =>
      `做了 ${total} 個蛋糕,平分裝進箱子,每個箱子剛好裝 ${part} 個,共裝了幾箱?`,
  },
  {
    op: 'div',
    unit: '次',
    build: (total, part) =>
      `玩一次投籃要 ${part} 枚代幣,小恩玩了幾次後共用了 ${total} 枚代幣,小恩玩了幾次?`,
  },
  {
    op: 'mul',
    unit: '元',
    build: (each, count) =>
      `有 ${count} 個人平分獎金,每人分到 ${each} 元,獎金一共有多少元?`,
  },
  {
    op: 'mul',
    unit: '顆',
    build: (each, count) =>
      `每 ${each} 顆串成一條手鍊,一共串成了 ${count} 條,共用了多少顆串珠?`,
  },
  {
    op: 'mul',
    unit: '卷',
    build: (each, count) =>
      `工廠把膠帶分裝在 ${count} 個箱子,每箱裝 ${each} 卷,工廠共生產了多少卷膠帶?`,
  },
];

/**
 * ⑤ 有餘數應用題情境池(策展閉集)。每情境固定餘數處理 mode:
 *   - 'ceil'(進位):要把全部 N 個運完/裝完,每車/船/箱載 d 個 → 需 ⌈N/d⌉ 輛。
 *   - 'floor'(捨去 + 剩餘):N 個平分,每人/每組拿 d 個 → 可分 ⌊N/d⌋ 份,剩 N mod d。
 * build(total, perGroup) 造句;answerUnit = 答案單位、remUnit = 剩餘量的單位。
 */
interface RemWordScenario {
  mode: 'ceil' | 'floor';
  /** 答案單位(車/船/箱 或 人/組/袋)。 */
  answerUnit: string;
  /** 剩餘量單位(floor 模式才用,= 被分物的單位)。 */
  remUnit: string;
  build: (total: number, perGroup: number) => string;
}

const REMWORD_SCENARIOS: readonly RemWordScenario[] = [
  {
    mode: 'ceil',
    answerUnit: '台',
    remUnit: '人',
    build: (total, per) =>
      `${total} 個小朋友要坐車去校外教學,每台遊覽車最多坐 ${per} 人,至少要租幾台遊覽車才坐得下?`,
  },
  {
    mode: 'ceil',
    answerUnit: '艘',
    remUnit: '人',
    build: (total, per) =>
      `碼頭有 ${total} 個遊客要過河,每艘小船最多載 ${per} 人,至少要開幾艘船才能把大家都載過去?`,
  },
  {
    mode: 'ceil',
    answerUnit: '箱',
    remUnit: '顆',
    build: (total, per) =>
      `果園採了 ${total} 顆蘋果,每個箱子裝 ${per} 顆,要把蘋果全部裝完,至少需要幾個箱子?`,
  },
  {
    mode: 'floor',
    answerUnit: '人',
    remUnit: '顆',
    build: (total, per) =>
      `有 ${total} 顆糖果,每人分 ${per} 顆,可以分給幾個人?還剩下幾顆?`,
  },
  {
    mode: 'floor',
    answerUnit: '條',
    remUnit: '顆',
    build: (total, per) =>
      `有 ${total} 顆串珠,每 ${per} 顆串成一條手鍊,可以串成幾條手鍊?還剩下幾顆串珠?`,
  },
  {
    mode: 'floor',
    answerUnit: '組',
    remUnit: '張',
    build: (total, per) =>
      `老師有 ${total} 張貼紙,每 ${per} 張分成一組,可以分成幾組?還剩下幾張?`,
  },
];

/**
 * ⑥ 兩步驟文字題情境池(策展閉集)。每情境固定 shape:
 *   - 'mul-div'(先乘再除):a 盒每盒 b 個,平分給 c 人 → (a×b)÷c。
 *   - 'add-div'(先加再分):原有 a 加買 b,平分給 c 人 → (a+b)÷c。
 * build(a, b, c) 造句;unit = 答案單位。第二步保證整除。
 */
interface TwoStepScenario {
  shape: 'mul-div' | 'add-div';
  unit: string;
  build: (a: number, b: number, c: number) => string;
}

const TWOSTEP_SCENARIOS: readonly TwoStepScenario[] = [
  {
    shape: 'mul-div',
    unit: '顆',
    build: (a, b, c) =>
      `有 ${a} 盒巧克力,每盒裝 ${b} 顆,把全部巧克力平分給 ${c} 個小朋友,每人可以分到幾顆?`,
  },
  {
    shape: 'mul-div',
    unit: '元',
    build: (a, b, c) =>
      `班上買了 ${a} 包文具,每包 ${b} 元,這些錢由 ${c} 個人平均分攤,每人要付多少元?`,
  },
  {
    shape: 'add-div',
    unit: '本',
    build: (a, b, c) =>
      `圖書角原本有 ${a} 本書,老師又買來 ${b} 本,平分放到 ${c} 個書架上,每個書架放幾本?`,
  },
  {
    shape: 'add-div',
    unit: '顆',
    build: (a, b, c) =>
      `哥哥有 ${a} 顆彈珠,妹妹有 ${b} 顆彈珠,兩人把彈珠合起來平分成 ${c} 份,每份有幾顆?`,
  },
];

/**
 * ③ 算式填空的逆運算對 —— 第一步運算 → 其逆(round-trip 回原數)。
 * 'div-then-mul' = N ÷ f → ? × f(先除再乘);'mul-then-div' = N × f → ? ÷ f。
 */
const ARROW_OPS = ['div-then-mul', 'mul-then-div'] as const;
type ArrowOp = (typeof ARROW_OPS)[number];

// ───────────────────────────── seeded RNG ─────────────────────────────

/**
 * 確定性 RNG —— mulberry32(與 math generators / 聽力卷同款,純函式可重現)。
 * 注入 seed,杜絕 Math.random。
 */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** [min, max] 的整數(含兩端)。 */
function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** 抽一個元素。 */
function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

// ───────────────────────────── 各題型生成 ─────────────────────────────

/**
 * ① 直式除法 + 驗算:約 40% 整除 / 60% 有餘(seeded,可重現)。被除數三位數(100–999),
 * 除數 2–9。整除題 answer=純商、prompt「= ?」;有餘題答商+餘、可驗算。
 */
function genCompute(rng: () => number, n: number): MathComputeItem[] {
  const items: MathComputeItem[] = [];
  for (let i = 0; i < n; i++) {
    const divisor = randInt(rng, MIN_DIVISOR, MAX_DIVISOR);
    // 40% 整除 / 60% 有餘(rng < 0.4 → 整除)。
    const exact = rng() < 0.4;
    const remainder = exact ? 0 : randInt(rng, 1, divisor - 1);
    const minQ = Math.ceil(100 / divisor);
    const maxQ = Math.floor((MAX_DIVIDEND - remainder) / divisor);
    const quotient = randInt(rng, Math.max(minQ, 11), Math.max(minQ, maxQ));
    const dividend = quotient * divisor + remainder;
    if (exact) {
      items.push({
        id: `c${i + 1}`,
        kind: 'compute',
        dividend,
        divisor,
        quotient,
        remainder: 0,
        prompt: `${dividend} ÷ ${divisor} = ?(請用直式計算並驗算)`,
        answer: `${quotient}`,
        check: `${quotient}×${divisor}=${dividend}`,
      });
    } else {
      items.push({
        id: `c${i + 1}`,
        kind: 'compute',
        dividend,
        divisor,
        quotient,
        remainder,
        prompt: `${dividend} ÷ ${divisor} = ? 餘 ?(請用直式計算並驗算)`,
        answer: `${quotient} 餘 ${remainder}`,
        check: `${quotient}×${divisor}=${quotient * divisor},${quotient * divisor}+${remainder}=${dividend}`,
      });
    }
  }
  return items;
}

/**
 * ② 乘法直式:三位數(100–999)× 一位數(2–9),積 ≤ 9999。答積,可驗算。
 * 被乘數上限 = ⌊9999/乘數⌋ 與 999 取小,保證積在範圍。
 */
function genMulVert(rng: () => number, n: number): MathMulVertItem[] {
  const items: MathMulVertItem[] = [];
  for (let i = 0; i < n; i++) {
    const factorB = randInt(rng, MIN_DIVISOR, MAX_DIVISOR);
    const maxA = Math.min(999, Math.floor(MAX_PRODUCT / factorB));
    const factorA = randInt(rng, 100, Math.max(100, maxA));
    const product = factorA * factorB;
    items.push({
      id: `m${i + 1}`,
      kind: 'mulvert',
      factorA,
      factorB,
      product,
      prompt: `${factorA} × ${factorB} = ?(請用直式計算)`,
      answer: `${product}`,
      check: `${factorA}×${factorB}=${product}`,
    });
  }
  return items;
}

/**
 * ③ 算式填空:`N →(÷f)→ ? →(×f)→ ?` 或 `N →(×f)→ ? →(÷f)→ ?`,
 * 第二步是第一步的逆運算 → 第二格答案永遠 = 起始 N(round-trip)。
 */
function genArrow(rng: () => number, n: number): MathArrowItem[] {
  const items: MathArrowItem[] = [];
  for (let i = 0; i < n; i++) {
    const which: ArrowOp = pick(rng, ARROW_OPS);
    const factor = randInt(rng, MIN_DIVISOR, MAX_DIVISOR);
    let start: number;
    let op: '×' | '÷';
    let mid: number;

    if (which === 'div-then-mul') {
      // start ÷ factor 必整除:start = factor × q(q 兩位數,start ≤ 999)。
      const maxQ = Math.floor(MAX_DIVIDEND / factor);
      const q = randInt(rng, 10, Math.max(10, Math.min(maxQ, 99)));
      start = factor * q;
      op = '÷';
      mid = start / factor;
    } else {
      // start × factor ≤ 9999;start 兩位數。
      const maxStart = Math.floor(MAX_PRODUCT / factor);
      start = randInt(rng, 10, Math.max(10, Math.min(maxStart, 99)));
      op = '×';
      mid = start * factor;
    }
    const inverseOp = op === '÷' ? '×' : '÷';
    const end = start; // round-trip。
    items.push({
      id: `a${i + 1}`,
      kind: 'arrow',
      start,
      op,
      factor,
      mid,
      end,
      prompt: `${start} →(${op}${factor})→ ? →(${inverseOp}${factor})→ ?`,
      answer: `${mid};${end}`,
    });
  }
  return items;
}

/**
 * ④ 倍數互換 / 乘除互逆:抽整除式 `a÷b=c`(a=b×c),問 `a÷c=?`(=b)與 `b×c=?`(=a)。
 * 多數為小數(b、c 都 2–9,a ≤ 81,好心算);約 1/3 拉成大數梯度
 * (a=b×bigC,bigC 兩位數,如 459÷9=51 → 51×9=?)增加難度梯度。
 */
function genInverse(rng: () => number, n: number): MathInverseItem[] {
  const items: MathInverseItem[] = [];
  for (let i = 0; i < n; i++) {
    const big = rng() < 0.34; // 約 1/3 大數梯度題。
    const divisorB = randInt(rng, MIN_DIVISOR, MAX_DIVISOR);
    let quotientC: number;
    if (big) {
      // c 為兩位數,product = b×c ≤ 999(被除數護欄)。
      const maxC = Math.min(99, Math.floor(MAX_DIVIDEND / divisorB));
      quotientC = randInt(rng, 11, Math.max(11, maxC));
    } else {
      quotientC = randInt(rng, MIN_DIVISOR, MAX_DIVISOR);
    }
    const product = divisorB * quotientC;
    items.push({
      id: `i${i + 1}`,
      kind: 'inverse',
      product,
      divisorB,
      quotientC,
      prompt: `已知 ${product} ÷ ${divisorB} = ${quotientC},那麼 ${product} ÷ ${quotientC} = ?、${divisorB} × ${quotientC} = ?`,
      answer: `${divisorB};${product}`,
    });
  }
  return items;
}

/**
 * ⑤ 有餘數應用題:從情境池抽 → 給「保證有餘」參數 → 通順題 + 餘數處理 + 可驗證答案。
 *   - ceil:答 = 商 + 1(全部裝完要多一份)。
 *   - floor:答 = 商(可分幾份),另記 remainder(剩多少)。
 * 除數(每份量)≥ 5(別「每車坐 2 人」);被除數兩~三位、≤ 999。
 */
function genRemWord(rng: () => number, n: number): MathRemWordItem[] {
  const items: MathRemWordItem[] = [];
  for (let i = 0; i < n; i++) {
    const scen = pick(rng, REMWORD_SCENARIOS);
    const divisor = randInt(rng, REMWORD_MIN_DIVISOR, MAX_DIVISOR);
    const remainder = randInt(rng, 1, divisor - 1);
    // 商兩位數(答案有意義);maxQ 須讓 q×d + r ≤ 999(含最大餘數)。
    const maxQ = Math.floor((MAX_DIVIDEND - remainder) / divisor);
    const quotient = randInt(rng, 10, Math.max(10, maxQ));
    const dividend = quotient * divisor + remainder;
    const value = scen.mode === 'ceil' ? quotient + 1 : quotient;
    const answer =
      scen.mode === 'ceil'
        ? `${value} ${scen.answerUnit}`
        : `${value} ${scen.answerUnit},剩 ${remainder} ${scen.remUnit}`;
    items.push({
      id: `r${i + 1}`,
      kind: 'remword',
      dividend,
      divisor,
      quotient,
      remainder,
      mode: scen.mode,
      value,
      unitName: scen.answerUnit,
      prompt: scen.build(dividend, divisor),
      answer,
    });
  }
  return items;
}

/**
 * ⑥ 兩步驟文字題:從情境池抽 → 真兩步(先乘再除 / 先加再分),逐步可驗證。
 *   - mul-div:total = a×b(a 盒 × b 個,total ≤ 999),再 ÷ c 整除。
 *   - add-div:total = a+b(兩數和 ≤ 999),再 ÷ c 整除。
 * 第二步除數 c 2–9,保證 total % c === 0(先選 c 與商,再回推 a/b)。
 */
function genTwoStep(rng: () => number, n: number): MathTwoStepItem[] {
  const items: MathTwoStepItem[] = [];
  for (let i = 0; i < n; i++) {
    const scen = pick(rng, TWOSTEP_SCENARIOS);
    const c = randInt(rng, MIN_DIVISOR, MAX_DIVISOR);
    let a: number;
    let b: number;
    let step1: number;
    let expr: string;
    if (scen.shape === 'mul-div') {
      // a 盒 × b 個 = step1,step1 須被 c 整除且 ≤ 999。
      // 先選 b(每盒,2–9),選 a(盒數,2–9),再調 step1 為 c 的倍數:
      // 直接挑能整除的組合 —— a×b 須 % c == 0。用「value(最終商)」回推。
      const value = randInt(rng, 2, 9); // 最終每人分到。
      step1 = value * c; // = a×b,須 ≤ 999。
      // 把 step1 拆成 a×b(a,b 皆 2–9 較自然);若拆不出就放大 value 重試上界保護。
      const factor = pickFactorPair(rng, step1);
      a = factor[0];
      b = factor[1];
      expr = `${a}×${b}÷${c}`;
      items.push({
        id: `t${i + 1}`,
        kind: 'twostep',
        shape: 'mul-div',
        a,
        b,
        c,
        step1,
        value,
        unitName: scen.unit,
        expr,
        prompt: scen.build(a, b, c),
        answer: `${value} ${scen.unit}`,
      });
    } else {
      // a + b = step1,step1 須被 c 整除且 ≤ 999。先選 value(商)→ step1 = value×c。
      const value = randInt(rng, 6, Math.min(99, Math.floor(MAX_DIVIDEND / c)));
      step1 = value * c;
      // 拆 step1 = a + b(a、b 皆 ≥ 1,兩位數較自然)。
      a = randInt(rng, 1, step1 - 1);
      b = step1 - a;
      expr = `(${a}+${b})÷${c}`;
      items.push({
        id: `t${i + 1}`,
        kind: 'twostep',
        shape: 'add-div',
        a,
        b,
        c,
        step1,
        value,
        unitName: scen.unit,
        expr,
        prompt: scen.build(a, b, c),
        answer: `${value} ${scen.unit}`,
      });
    }
  }
  return items;
}

/**
 * 把一個 ≤ 999 的數拆成兩個 2–9 的因數對(供 mul-div 兩步題 a×b)。
 * 找不到(質數或無 2–9 分解)時 fallback `[1, n]`(仍算式正確,a×b=n)。
 */
function pickFactorPair(rng: () => number, n: number): [number, number] {
  const pairs: Array<[number, number]> = [];
  for (let x = 2; x <= 9; x++) {
    if (n % x === 0) {
      const y = n / x;
      if (y >= 2 && y <= 99) pairs.push([x, y]);
    }
  }
  if (pairs.length === 0) return [1, n];
  return pick(rng, pairs);
}

/**
 * ⑦ 應用題:從情境池抽,給整除參數 → 通順題 + 算式 + 數值答案 + 單位。
 * 除法情境保證整除;單價型(priced)除數用較高範圍(5–20),避免「每包 2 元」。
 * 乘法情境積 ≤ 9999。
 */
function genWord(rng: () => number, n: number): MathWordItem[] {
  const items: MathWordItem[] = [];
  for (let i = 0; i < n; i++) {
    const scen = pick(rng, WORD_SCENARIOS);
    if (scen.op === 'div') {
      // 單價型除數用 5–20(別「每包 2 元」);一般除數 2–9。整除、商兩位數。
      const part = scen.priced
        ? randInt(rng, 5, 20)
        : randInt(rng, MIN_DIVISOR, MAX_DIVISOR);
      const quotient = randInt(rng, 10, Math.floor(MAX_DIVIDEND / part));
      const total = part * quotient;
      items.push({
        id: `w${i + 1}`,
        kind: 'word',
        prompt: scen.build(total, part),
        expr: `${total}÷${part}`,
        value: quotient,
        unitName: scen.unit,
        answer: `${quotient} ${scen.unit}`,
      });
    } else {
      // each × count,積 ≤ 9999;count 2–9、each 兩位~三位。
      const count = randInt(rng, MIN_DIVISOR, MAX_DIVISOR);
      const each = randInt(rng, 10, Math.floor(MAX_PRODUCT / count));
      const total = each * count;
      items.push({
        id: `w${i + 1}`,
        kind: 'word',
        prompt: scen.build(each, count),
        expr: `${each}×${count}`,
        value: total,
        unitName: scen.unit,
        answer: `${total} ${scen.unit}`,
      });
    }
  }
  return items;
}

// ───────────────────────────── 組卷 ─────────────────────────────

/**
 * 各大題題數(預設)。Σ(pointsEach×count) = 100(正式段考卷):
 *   compute 5×4=20 / mulvert 3×4=12 / arrow 2×4=8 / inverse 2×4=8 /
 *   remword 4×4=16 / twostep 4×4=16 / word 4×5=20 → 100。
 */
const DEFAULT_COUNTS: Required<SectionCounts> = {
  compute: 5,
  mulvert: 3,
  arrow: 2,
  inverse: 2,
  remword: 4,
  twostep: 4,
  word: 4,
};

/** 七大題的固定出題順序(段考卷由淺入深)。 */
const SECTION_ORDER: MathSectionKind[] = [
  'compute',
  'mulvert',
  'arrow',
  'inverse',
  'remword',
  'twostep',
  'word',
];

/**
 * 各大題的元資料(標籤 / 標題 / 指示 / 配分)。
 * arrow / inverse 是 2 格題,pointsEach 用偶數讓每格整數(每格 2 分)。
 */
const SECTION_META: Record<
  MathSectionKind,
  { title: string; instructions: string; pointsEach: number }
> = {
  compute: {
    title: '直式除法與驗算',
    instructions: '用直式計算下列各題,並寫出驗算(有餘數的要寫出餘數)。每題 4 分。',
    pointsEach: 4,
  },
  mulvert: {
    title: '乘法直式',
    instructions: '用直式計算下列各題,算出乘積。每題 4 分。',
    pointsEach: 4,
  },
  arrow: {
    title: '算式填空',
    instructions: '依照箭頭的運算,把每個空格填上正確的數。每格 2 分。',
    pointsEach: 4,
  },
  inverse: {
    title: '乘除互換',
    instructions: '利用乘除互逆,算出空格中的數。每格 2 分。',
    pointsEach: 4,
  },
  remword: {
    title: '有餘數的應用',
    instructions: '先列出算式,判斷餘數要進位或剩下,再寫出答案(記得寫單位)。每題 4 分。',
    pointsEach: 4,
  },
  twostep: {
    title: '兩步驟應用',
    instructions: '先想清楚要算兩步,列出算式,再算出答案(記得寫單位)。每題 4 分。',
    pointsEach: 4,
  },
  word: {
    title: '應用題',
    instructions: '先列出算式,再算出答案(記得寫單位)。每題 5 分。',
    pointsEach: 5,
  },
};

const CN_SECTION_LABELS = ['一', '二', '三', '四', '五', '六', '七', '八'];

const SECTION_GENERATORS: Record<
  MathSectionKind,
  (rng: () => number, n: number) => MathExamItem[]
> = {
  compute: genCompute,
  mulvert: genMulVert,
  arrow: genArrow,
  inverse: genInverse,
  remword: genRemWord,
  twostep: genTwoStep,
  word: genWord,
};

/**
 * 從範本生一份新的同型數學考卷。同 seed → 同卷(可重現)。
 *
 * @param opts seed(必填)+ 可選題數 / kps 子集 / id / title。
 * @returns 一份 `MathExam`(預設七大題,總分 100)。生成後建議過 `validateMathExam`。
 */
export function generateMathExam(opts: MathGenOpts): MathExam {
  const rng = makeRng(opts.seed);
  const counts: Required<SectionCounts> = { ...DEFAULT_COUNTS, ...opts.counts };
  const order: MathSectionKind[] =
    opts.kps && opts.kps.length > 0 ? opts.kps : SECTION_ORDER;

  const sections: MathSection[] = order.map((kind, idx) => {
    const meta = SECTION_META[kind];
    const n = counts[kind];
    return {
      kind,
      label: CN_SECTION_LABELS[idx] ?? String(idx + 1),
      title: meta.title,
      instructions: meta.instructions,
      pointsEach: meta.pointsEach,
      items: SECTION_GENERATORS[kind](rng, n),
    };
  });

  return {
    id: opts.id ?? `math-${opts.seed}`,
    title: opts.title ?? `數學考卷 · 第 ${opts.seed} 回`,
    textbook: '康軒',
    scope: '三下 · 乘法與除法',
    sections,
  };
}

/** 一份考卷的總分(Σ pointsEach×題數)。 */
export function examTotalPoints(exam: MathExam): number {
  return exam.sections.reduce(
    (sum, s) => sum + s.pointsEach * s.items.length,
    0,
  );
}

// ───────────────────────────── 驗證器 ─────────────────────────────

/**
 * 驗證一份(產生)數學考卷是否合法。回**錯誤訊息清單**,空陣列 = 合法。
 *
 * 檢查(lockdown 不打地鼠):
 *   - id 唯一、prompt 非空、每大題至少一題。
 *   - ① compute:除數 2–9、被除數 ≤ 999、餘數 0..除數-1、a=b×q+r、答案字串相符。
 *   - ② mulvert:被乘數三位、乘數 2–9、a×b=積、積 ≤ 9999、答案相符。
 *   - ③ arrow:factor 2–9、整除 / 積在範圍、第二格 round-trip 回 start、答案字串相符。
 *   - ④ inverse:a=b×c、b 2–9、c 2–99、product ≤ 999、答案字串相符。
 *   - ⑤ remword:a=b×q+r、有餘數、除數 ≥ 5、value=進位/捨去正確、答案字串相符。
 *   - ⑥ twostep:step1 重算正確、step1 % c == 0、value=step1/c、答案字串相符。
 *   - ⑦ word:算式重算 == value、value > 0、除法整除、乘積 ≤ 9999、答案字串相符。
 */
export function validateMathExam(exam: MathExam): string[] {
  const errors: string[] = [];
  const seenIds = new Set<string>();

  if (exam.sections.length === 0) errors.push('考卷沒有任何大題');

  for (const section of exam.sections) {
    if (section.items.length === 0) {
      errors.push(`${section.label}、${section.title}:沒有任何題目`);
    }
    for (const item of section.items) {
      if (seenIds.has(item.id)) errors.push(`${item.id}: 重複的 item id`);
      seenIds.add(item.id);
      if (!item.prompt || item.prompt.trim() === '') {
        errors.push(`${item.id}: prompt 為空`);
        continue;
      }
      switch (item.kind) {
        case 'compute':
          validateCompute(item, errors);
          break;
        case 'mulvert':
          validateMulVert(item, errors);
          break;
        case 'arrow':
          validateArrow(item, errors);
          break;
        case 'inverse':
          validateInverse(item, errors);
          break;
        case 'remword':
          validateRemWord(item, errors);
          break;
        case 'twostep':
          validateTwoStep(item, errors);
          break;
        case 'word':
          validateWord(item, errors);
          break;
      }
    }
  }

  return errors;
}

function inG3Divisor(n: number): boolean {
  return Number.isInteger(n) && n >= MIN_DIVISOR && n <= MAX_DIVISOR;
}

function validateCompute(it: MathComputeItem, errors: string[]): void {
  if (!inG3Divisor(it.divisor)) {
    errors.push(`${it.id}: 除數 ${it.divisor} 不在 G3 範圍(2–9)`);
  }
  if (it.dividend < 1 || it.dividend > MAX_DIVIDEND) {
    errors.push(`${it.id}: 被除數 ${it.dividend} 超出 G3 範圍(1–${MAX_DIVIDEND})`);
  }
  if (it.divisor === 0) {
    errors.push(`${it.id}: 除以 0`);
    return;
  }
  // 餘數:0(整除)或 1..除數-1(有餘)。
  if (it.remainder < 0 || it.remainder >= it.divisor) {
    errors.push(`${it.id}: 餘數 ${it.remainder} 非法(須 0..${it.divisor - 1})`);
  }
  // 反算:a = b×q + r。
  if (it.divisor * it.quotient + it.remainder !== it.dividend) {
    errors.push(
      `${it.id}: 反算失敗 ${it.divisor}×${it.quotient}+${it.remainder} ≠ ${it.dividend}`,
    );
  }
  const expectedAnswer =
    it.remainder === 0 ? `${it.quotient}` : `${it.quotient} 餘 ${it.remainder}`;
  if (it.answer !== expectedAnswer) {
    errors.push(`${it.id}: answer 字串「${it.answer}」與商餘不符`);
  }
}

function validateMulVert(it: MathMulVertItem, errors: string[]): void {
  if (!inG3Divisor(it.factorB)) {
    errors.push(`${it.id}: 乘數 ${it.factorB} 不在 G3 範圍(2–9)`);
  }
  if (it.factorA < 100 || it.factorA > 999) {
    errors.push(`${it.id}: 被乘數 ${it.factorA} 非三位數(100–999)`);
  }
  if (it.factorA * it.factorB !== it.product) {
    errors.push(`${it.id}: 反算失敗 ${it.factorA}×${it.factorB} ≠ ${it.product}`);
  }
  if (it.product > MAX_PRODUCT) {
    errors.push(`${it.id}: 乘積 ${it.product} 超出 G3 範圍(≤ ${MAX_PRODUCT})`);
  }
  if (it.answer !== `${it.product}`) {
    errors.push(`${it.id}: answer 字串「${it.answer}」與乘積不符`);
  }
}

function validateArrow(it: MathArrowItem, errors: string[]): void {
  if (!inG3Divisor(it.factor)) {
    errors.push(`${it.id}: 倍數/除數 ${it.factor} 不在 G3 範圍(2–9)`);
  }
  // 第一格反算。
  let expectedMid: number;
  if (it.op === '÷') {
    if (it.start % it.factor !== 0) {
      errors.push(`${it.id}: ${it.start} ÷ ${it.factor} 不整除`);
    }
    expectedMid = it.start / it.factor;
  } else {
    expectedMid = it.start * it.factor;
    if (expectedMid > MAX_PRODUCT) {
      errors.push(`${it.id}: 乘積 ${expectedMid} 超出 G3 範圍(≤ ${MAX_PRODUCT})`);
    }
  }
  if (it.mid !== expectedMid) {
    errors.push(`${it.id}: 第一格 ${it.mid} ≠ 反算 ${expectedMid}`);
  }
  // round-trip:第二格必回 start。
  if (it.end !== it.start) {
    errors.push(`${it.id}: round-trip 第二格 ${it.end} ≠ 起始 ${it.start}`);
  }
  if (it.start < 1 || it.start > MAX_DIVIDEND) {
    errors.push(`${it.id}: 起始數 ${it.start} 超出 G3 範圍(1–${MAX_DIVIDEND})`);
  }
  if (it.answer !== `${it.mid};${it.end}`) {
    errors.push(`${it.id}: answer 字串「${it.answer}」與兩格不符`);
  }
}

function validateInverse(it: MathInverseItem, errors: string[]): void {
  if (!inG3Divisor(it.divisorB)) {
    errors.push(`${it.id}: 除數 ${it.divisorB} 不在 G3 範圍(2–9)`);
  }
  // 商 c 可為兩位數(大數梯度題),但須 2–99 且 product ≤ 999。
  if (!Number.isInteger(it.quotientC) || it.quotientC < 2 || it.quotientC > 99) {
    errors.push(`${it.id}: 商 ${it.quotientC} 不在範圍(2–99)`);
  }
  if (it.product > MAX_DIVIDEND) {
    errors.push(`${it.id}: 被除數 ${it.product} 超出 G3 範圍(≤ ${MAX_DIVIDEND})`);
  }
  if (it.divisorB * it.quotientC !== it.product) {
    errors.push(
      `${it.id}: 反算失敗 ${it.divisorB}×${it.quotientC} ≠ ${it.product}`,
    );
  }
  if (it.answer !== `${it.divisorB};${it.product}`) {
    errors.push(`${it.id}: answer 字串「${it.answer}」與互逆結果不符`);
  }
}

function validateRemWord(it: MathRemWordItem, errors: string[]): void {
  if (!inG3Divisor(it.divisor) || it.divisor < REMWORD_MIN_DIVISOR) {
    errors.push(`${it.id}: 除數 ${it.divisor} 不在範圍(${REMWORD_MIN_DIVISOR}–${MAX_DIVISOR})`);
  }
  if (it.dividend < 1 || it.dividend > MAX_DIVIDEND) {
    errors.push(`${it.id}: 被除數 ${it.dividend} 超出 G3 範圍(1–${MAX_DIVIDEND})`);
  }
  // 反算:a = b×q + r,且保證有餘數(情境才成立)。
  if (it.divisor * it.quotient + it.remainder !== it.dividend) {
    errors.push(
      `${it.id}: 反算失敗 ${it.divisor}×${it.quotient}+${it.remainder} ≠ ${it.dividend}`,
    );
  }
  if (it.remainder < 1 || it.remainder >= it.divisor) {
    errors.push(`${it.id}: 餘數 ${it.remainder} 非法(有餘情境須 1..${it.divisor - 1})`);
  }
  // 進位 / 捨去判斷正確。
  const expectedValue = it.mode === 'ceil' ? it.quotient + 1 : it.quotient;
  if (it.value !== expectedValue) {
    errors.push(
      `${it.id}: ${it.mode} 答案 ${it.value} 不符(應為 ${expectedValue})`,
    );
  }
  const expectedAnswer =
    it.mode === 'ceil'
      ? `${it.value} ${it.unitName}`
      : `${it.value} ${it.unitName},剩 ${it.remainder} `;
  // floor 的剩餘單位由情境決定,只比對前綴(value+單位+「,剩 R 」)。
  if (it.mode === 'ceil') {
    if (it.answer !== expectedAnswer) {
      errors.push(`${it.id}: answer 字串「${it.answer}」與進位答案不符`);
    }
  } else {
    if (!it.answer.startsWith(expectedAnswer)) {
      errors.push(`${it.id}: answer 字串「${it.answer}」與捨去/剩餘答案不符`);
    }
  }
  if (!it.unitName || it.unitName.trim() === '') {
    errors.push(`${it.id}: 缺單位`);
  }
}

function validateTwoStep(it: MathTwoStepItem, errors: string[]): void {
  // 第一步重算。
  const expectedStep1 = it.shape === 'mul-div' ? it.a * it.b : it.a + it.b;
  if (it.step1 !== expectedStep1) {
    errors.push(`${it.id}: 第一步 ${it.step1} ≠ 重算 ${expectedStep1}`);
  }
  if (it.step1 > MAX_DIVIDEND) {
    errors.push(`${it.id}: 中間結果 ${it.step1} 超出 G3 範圍(≤ ${MAX_DIVIDEND})`);
  }
  if (!inG3Divisor(it.c)) {
    errors.push(`${it.id}: 第二步除數 ${it.c} 不在 G3 範圍(2–9)`);
  }
  // 第二步整除。
  if (it.c === 0 || it.step1 % it.c !== 0) {
    errors.push(`${it.id}: 第二步 ${it.step1} ÷ ${it.c} 不整除`);
  } else if (it.step1 / it.c !== it.value) {
    errors.push(`${it.id}: 最終答案 ${it.value} ≠ ${it.step1}÷${it.c}`);
  }
  if (it.value <= 0) {
    errors.push(`${it.id}: 答案 ${it.value} ≤ 0`);
  }
  if (it.answer !== `${it.value} ${it.unitName}`) {
    errors.push(`${it.id}: answer 字串「${it.answer}」與 value+單位不符`);
  }
  if (!it.unitName || it.unitName.trim() === '') {
    errors.push(`${it.id}: 缺單位`);
  }
}

/** 解析簡單算式 `a÷b` 或 `a×b` → 數值結果;非法回 null。 */
function evalExpr(expr: string): number | null {
  const div = expr.split('÷');
  if (div.length === 2) {
    const a = Number(div[0]);
    const b = Number(div[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
    if (a % b !== 0) return null; // 應用題答案應整除(平分情境)。
    return a / b;
  }
  const mul = expr.split('×');
  if (mul.length === 2) {
    const a = Number(mul[0]);
    const b = Number(mul[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return a * b;
  }
  return null;
}

function validateWord(it: MathWordItem, errors: string[]): void {
  const computed = evalExpr(it.expr);
  if (computed === null) {
    errors.push(`${it.id}: 算式「${it.expr}」非法或不整除`);
    return;
  }
  if (computed !== it.value) {
    errors.push(`${it.id}: 算式「${it.expr}」=${computed} ≠ value ${it.value}`);
  }
  if (it.value <= 0) {
    errors.push(`${it.id}: 答案 ${it.value} ≤ 0`);
  }
  if (it.expr.includes('×') && it.value > MAX_PRODUCT) {
    errors.push(`${it.id}: 乘積 ${it.value} 超出 G3 範圍(≤ ${MAX_PRODUCT})`);
  }
  if (!it.unitName || it.unitName.trim() === '') {
    errors.push(`${it.id}: 缺單位`);
  }
  if (it.answer !== `${it.value} ${it.unitName}`) {
    errors.push(`${it.id}: answer 字串「${it.answer}」與 value+單位不符`);
  }
}
